import {produce} from 'immer';
import {v4 as uuidv4} from 'uuid';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {getVariablePairLabelFromDomainLabels} from 'antibodies/AntibodyUtils';
import {ProteinCreationStructuredTable} from 'antibodies/nested-data-creation/full-screen/ProteinCreationStructuredTableAdapter';
import {
  ProteinCreationCellValue,
  ProteinCreationCellValueType,
  ProteinCreationRow,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {
  CountsOfCreatedComponents,
  generateSpreadsheetTemplateHeaders,
  getCountsOfCreatedComponents,
} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  NdcColumnState,
  NdcComponentInputType,
  NdcComponentSequenceType,
  NdcComponentType,
  NdcSpreadsheetComponentFormatType,
  NdcSpreadsheetInterpretation,
  TaggedChainKeyDict,
  TaggedDomainKeyDict,
  TaggedVariablePairKeyDict,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isTagValueStructuredTableColumn} from 'notebook/structured-tables/columns/TypePredicates';
import {
  StructuredTableColumn,
  StructuredTableRow,
  TagValueStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {formatTypedValue, parseTextAsFloat, parseTextAsInteger} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';
import {assertNever, Optional} from 'util/Types';

function isSchemaFieldColumn(
  column: StructuredTableColumn
): column is TagValueStructuredTableColumn & {schemaFieldApiId: string} {
  return isTagValueStructuredTableColumn(column) && Boolean(column.schemaFieldApiId);
}

function isNameColumn(column: StructuredTableColumn): boolean {
  return column.columnType === TableColumnType.BIOENTITY_NAME;
}

function isAliasColumn(column: StructuredTableColumn): boolean {
  return column.columnType === TableColumnType.ALIAS;
}

function textCellToValue(text: Optional<string>): ProteinCreationCellValue {
  if (text != null && text !== '') {
    return {type: ProteinCreationCellValueType.RAW_TEXT, text};
  }
  return {type: ProteinCreationCellValueType.EMPTY};
}

/** Convert a nested component value to a structured table Cell. */
function nestedComponentValueToCell(value: ProteinCreationCellValue): Cell {
  switch (value.type) {
    case ProteinCreationCellValueType.EXISTING_ENTITY:
      return {
        text: '',
        typedValue: {
          type: BaseTypedValueType.BIOENTITY,
          bioentitySummary: {id: value.entityId, name: value.displayName},
        },
      };
    case ProteinCreationCellValueType.RAW_TEXT:
      return {text: value.text};
    case ProteinCreationCellValueType.EMPTY:
    case ProteinCreationCellValueType.BOOLEAN:
    case ProteinCreationCellValueType.NUMBER:
    case ProteinCreationCellValueType.ENTITY_LINK:
    case ProteinCreationCellValueType.DROPDOWN_OPTION:
      return {text: ''};
    default:
      assertNever(value);
      return {text: ''};
  }
}

/** Parse a nested component column Cell into a ProteinCreationCellValue. */
function cellToNestedComponentValue(cell: Cell): ProteinCreationCellValue {
  if (cell.typedValue?.type === BaseTypedValueType.BIOENTITY) {
    return {
      type: ProteinCreationCellValueType.EXISTING_ENTITY,
      entityId: cell.typedValue.bioentitySummary.id,
      displayName: cell.typedValue.bioentitySummary.name,
    };
  }
  return textCellToValue(cell.text);
}

/**
 * Pasting a typed cell, such as an Alias cell from a registration table, keeps its typed value and clears its
 * text. Several pasted aliases become one comma-separated alias that stays visible for the user to edit.
 */
function cellToText(cell: Cell): string {
  if (cell.text) {
    return cell.text;
  }
  return cell.typedValue != null ? formatTypedValue(cell.typedValue) : '';
}

/** Convert a schema field value to a structured table Cell in the shape its TAG_VALUE editor writes. */
function schemaFieldValueToCell(value: ProteinCreationCellValue): Cell {
  switch (value.type) {
    case ProteinCreationCellValueType.ENTITY_LINK:
      return {
        text: '',
        typedValue: {
          type: FieldType.BIOENTITY_LINK,
          value: null,
          targetFiles: value.entities.map(({id, name, url}) => ({id, name, url})),
        },
      };
    case ProteinCreationCellValueType.BOOLEAN:
      return {
        text: '',
        typedValue: {type: FieldType.BOOLEAN, value: null, booleanValue: value.value},
      };
    case ProteinCreationCellValueType.DROPDOWN_OPTION:
      return {
        text: '',
        typedValue: {
          type: FieldType.SELECTOR,
          value: value.name,
          schemaFieldSelectorOptionIds: [value.optionId],
        },
      };
    case ProteinCreationCellValueType.NUMBER:
    case ProteinCreationCellValueType.RAW_TEXT:
      return {text: value.text};
    case ProteinCreationCellValueType.EMPTY:
    case ProteinCreationCellValueType.EXISTING_ENTITY:
      return {text: ''};
    default:
      assertNever(value);
      return {text: ''};
  }
}

function parseNumberText(text: string, fieldType: FieldType | null): number | null {
  if (fieldType === FieldType.INTEGER) {
    return parseTextAsInteger(text);
  }
  if (fieldType === FieldType.FLOAT) {
    return parseTextAsFloat(text);
  }
  return null;
}

/**
 * Parse a schema field column Cell into a ProteinCreationCellValue.
 * Number text that fails to parse stays RAW_TEXT so the user's input is preserved and reported by validation.
 */
function cellToSchemaFieldValue(cell: Cell, fieldType: FieldType | null): ProteinCreationCellValue {
  const {typedValue, text} = cell;
  if (typedValue?.type === FieldType.BIOENTITY_LINK) {
    const entities = typedValue.targetFiles.flatMap(({id, name, url}) =>
      id == null ? [] : [{id, name, url}]
    );
    return entities.length > 0
      ? {type: ProteinCreationCellValueType.ENTITY_LINK, entities}
      : {type: ProteinCreationCellValueType.EMPTY};
  }
  if (typedValue?.type === FieldType.BOOLEAN) {
    return typedValue.booleanValue == null
      ? {type: ProteinCreationCellValueType.EMPTY}
      : {type: ProteinCreationCellValueType.BOOLEAN, value: typedValue.booleanValue};
  }
  if (typedValue?.type === FieldType.SELECTOR) {
    // Only single-select dropdown columns are shown, so the cell is assumed to hold at most one option.
    const optionId = typedValue.schemaFieldSelectorOptionIds?.[0];
    return optionId == null
      ? {type: ProteinCreationCellValueType.EMPTY}
      : {type: ProteinCreationCellValueType.DROPDOWN_OPTION, optionId, name: typedValue.value ?? ''};
  }
  if (text != null && text !== '') {
    const numberValue = parseNumberText(text, fieldType);
    if (numberValue != null) {
      return {type: ProteinCreationCellValueType.NUMBER, value: numberValue, text};
    }
  }
  return textCellToValue(text);
}

/** Create a row with the value returned by `getValue` for every column in the table. */
export function createRow(
  columns: ReadonlyArray<StructuredTableColumn>,
  getValue: (column: StructuredTableColumn) => ProteinCreationCellValue
): ProteinCreationRow {
  let name = '';
  const nestedComponents: Record<string, ProteinCreationCellValue> = {};
  const schemaFields: Record<string, ProteinCreationCellValue> = {};
  let alias = '';
  for (const column of columns) {
    if (isNameColumn(column)) {
      const value = getValue(column);
      name = value.type === ProteinCreationCellValueType.RAW_TEXT ? value.text : '';
    } else if (isAliasColumn(column)) {
      const value = getValue(column);
      alias = value.type === ProteinCreationCellValueType.RAW_TEXT ? value.text : '';
    } else if (isSchemaFieldColumn(column)) {
      schemaFields[column.schemaFieldApiId] = getValue(column);
    } else {
      nestedComponents[column.name] = getValue(column);
    }
  }
  return {cid: uuidv4(), name, nestedComponents, schemaFields, alias};
}

/** Create a blank row with empty values for every column in the table. */
export function createBlankRow(columns: ReadonlyArray<StructuredTableColumn>): ProteinCreationRow {
  return createRow(columns, () => ({type: ProteinCreationCellValueType.EMPTY}));
}

/**
 * Derive a StructuredTable from ProteinCreationRows.
 * Iterates columns to map ProteinCreationRow dicts to positional cells.
 */
export function proteinCreationRowsToStructuredTable(
  baseTable: ProteinCreationStructuredTable,
  rows: Array<ProteinCreationRow>
): ProteinCreationStructuredTable {
  const {columns} = baseTable;

  return produce(baseTable, (draft) => {
    draft.rows = rows.map((row) => {
      const cells: Array<Cell> = columns.map((column) => {
        if (isNameColumn(column)) {
          return {text: row.name};
        }
        if (isAliasColumn(column)) {
          return {text: row.alias};
        }
        if (isSchemaFieldColumn(column)) {
          const value = row.schemaFields[column.schemaFieldApiId];
          return value ? schemaFieldValueToCell(value) : {text: ''};
        }
        const value = row.nestedComponents[column.name];
        return value ? nestedComponentValueToCell(value) : {text: ''};
      });
      return {
        isFinalized: false,
        rowId: row.cid,
        cells,
        metadata: {},
      };
    });
  });
}

/** Whether any cell in the table, including name, alias and schema field cells, has a value. */
export function tableHasData(rows: ReadonlyArray<ProteinCreationRow>): boolean {
  return rows.some(
    (row) =>
      row.name !== '' ||
      row.alias !== '' ||
      [...Object.values(row.nestedComponents), ...Object.values(row.schemaFields)].some(
        (v) => v.type !== ProteinCreationCellValueType.EMPTY
      )
  );
}

export function isNonEmptyRow(row: ProteinCreationRow): boolean {
  return Object.values(row.nestedComponents).some((v) => v.type !== ProteinCreationCellValueType.EMPTY);
}

/** Map each ref on the antibody glyph to the label of the component column it belongs to. */
export function getComponentColumnLabelByRef(
  format: AntibodyFormat,
  startingType: NdcSpreadsheetComponentFormatType
): Map<number, string> {
  const labelByRef = new Map<number, string>();
  if (startingType === NdcSpreadsheetComponentFormatType.CHAINS) {
    for (const chain of format.chains) {
      for (const ref of [...chain.refs, ...chain.domainRefsPerChain.flat()]) {
        labelByRef.set(ref, chain.label);
      }
    }
    return labelByRef;
  }
  for (const domain of format.domains) {
    for (const ref of domain.refs) {
      labelByRef.set(ref, domain.label);
    }
  }
  if (startingType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS) {
    for (const pair of format.distinctVariablePairs) {
      for (const ref of [...pair.vhVaVg.refs, ...pair.vlVbVd.refs]) {
        labelByRef.set(ref, pair.label);
      }
    }
  }
  return labelByRef;
}

/**
 * Replace raw text values with the values the table evaluator resolved them to: existing entities in
 * nested component columns, and entity links and dropdown options in schema field columns.
 * Pasted names only resolve in the computed table, so rows parsed from table updates still hold the text.
 * Dropdown options pasted from another dropdown are likewise replaced with the option they resolve to.
 */
export function mergeResolvedEntitiesIntoRows(
  rows: Array<ProteinCreationRow>,
  columns: ReadonlyArray<StructuredTableColumn>,
  computedTable: ReadonlyArray<ReadonlyArray<EvaluatedCell>> | null
): Array<ProteinCreationRow> {
  if (computedTable == null) {
    return rows;
  }
  return produce(rows, (draft) => {
    draft.forEach((row, rowIndex) => {
      columns.forEach((column, columnIndex) => {
        const resolvedValue = computedTable[rowIndex]?.[columnIndex]?.value;
        if (isSchemaFieldColumn(column)) {
          const rowValue = row.schemaFields[column.schemaFieldApiId];
          if (
            rowValue?.type === ProteinCreationCellValueType.RAW_TEXT &&
            (resolvedValue?.type === FieldType.BIOENTITY_LINK || resolvedValue?.type === FieldType.SELECTOR)
          ) {
            const value = cellToSchemaFieldValue(
              {text: '', typedValue: resolvedValue},
              column.schemaFieldType
            );
            if (
              value.type === ProteinCreationCellValueType.ENTITY_LINK ||
              value.type === ProteinCreationCellValueType.DROPDOWN_OPTION
            ) {
              row.schemaFields[column.schemaFieldApiId] = value;
            }
          } else if (
            rowValue?.type === ProteinCreationCellValueType.DROPDOWN_OPTION &&
            resolvedValue?.type === FieldType.SELECTOR
          ) {
            // A dropdown cell pasted from another dropdown keeps that dropdown's option, which the evaluator
            // resolves by name to an option of this column's dropdown.
            const value = cellToSchemaFieldValue(
              {text: '', typedValue: resolvedValue},
              column.schemaFieldType
            );
            if (
              value.type === ProteinCreationCellValueType.DROPDOWN_OPTION &&
              value.optionId !== rowValue.optionId
            ) {
              row.schemaFields[column.schemaFieldApiId] = value;
            }
          }
          return;
        }
        if (
          row.nestedComponents[column.name]?.type === ProteinCreationCellValueType.RAW_TEXT &&
          resolvedValue?.type === BaseTypedValueType.BIOENTITY
        ) {
          row.nestedComponents[column.name] = {
            type: ProteinCreationCellValueType.EXISTING_ENTITY,
            entityId: resolvedValue.bioentitySummary.id,
            displayName: resolvedValue.bioentitySummary.name,
          };
        }
      });
    });
  });
}

/**
 * Parse a StructuredTable's rows back into ProteinCreationRows.
 */
export function parseStructuredTableToRows(
  structuredTableRows: ReadonlyArray<StructuredTableRow<RowMetadata>>,
  baseColumns: ReadonlyArray<StructuredTableColumn>,
  existingRows: Array<ProteinCreationRow>
): Array<ProteinCreationRow> {
  const existingRowByCid = new Map(existingRows.map((row) => [row.cid, row]));
  return structuredTableRows.map((structuredRow) => {
    const existingRow = existingRowByCid.get(structuredRow.rowId);
    let name = '';
    const nestedComponents: Record<string, ProteinCreationCellValue> = {};
    const schemaFields: Record<string, ProteinCreationCellValue> = {};
    let alias = '';
    baseColumns.forEach((column, i) => {
      const cell = structuredRow.cells[i];
      if (isNameColumn(column)) {
        name = cellToText(cell);
      } else if (isAliasColumn(column)) {
        alias = cellToText(cell);
      } else if (isSchemaFieldColumn(column)) {
        schemaFields[column.schemaFieldApiId] = cellToSchemaFieldValue(cell, column.schemaFieldType);
      } else {
        nestedComponents[column.name] = cellToNestedComponentValue(cell);
      }
    });
    return {
      cid: existingRow?.cid ?? uuidv4(),
      name,
      nestedComponents,
      schemaFields,
      alias,
    };
  });
}

// --- NDC interpretation ---

const DNA_BASES_REGEX = /^[ATGCatgc\s]+$/;

export function isDnaRawText(text: string): boolean {
  return DNA_BASES_REGEX.test(text.trim());
}

/**
 * Classify a nested component cell.
 * Existing pair type can't be classified by ID, so DNA pairs must be pre-fetched
 * and passed in as `dnaPairEntityIds`.
 */
export function classifyCellValue(
  value: ProteinCreationCellValue,
  dnaPairEntityIds: ReadonlySet<string> = new Set()
): NdcComponentType | null {
  switch (value.type) {
    case ProteinCreationCellValueType.RAW_TEXT:
      return {
        componentSequenceType: isDnaRawText(value.text)
          ? NdcComponentSequenceType.DNA
          : NdcComponentSequenceType.AA,
        componentInputType: NdcComponentInputType.NEW_SEQUENCE,
      };
    case ProteinCreationCellValueType.EXISTING_ENTITY: {
      const isDna =
        APIUtil.apiIdToType(value.entityId) === ModelTypeConstants.SEQUENCE ||
        dnaPairEntityIds.has(value.entityId);
      return {
        componentSequenceType: isDna ? NdcComponentSequenceType.DNA : NdcComponentSequenceType.AA,
        componentInputType: NdcComponentInputType.EXISTING_ENTITY_NAME_OR_REGISTRY_ID,
      };
    }
    case ProteinCreationCellValueType.EMPTY:
    case ProteinCreationCellValueType.BOOLEAN:
    case ProteinCreationCellValueType.NUMBER:
    case ProteinCreationCellValueType.ENTITY_LINK:
    case ProteinCreationCellValueType.DROPDOWN_OPTION:
      return null;
    default:
      return null;
  }
}

export interface ProteinCreationRowsSummary {
  hasData: boolean;
  nonEmptyRowCount: number;
  hasDnaInputs: boolean;
}

/**
 * Summarize the table in one pass over its component cells. `hasDnaInputs` is true if any cell holds new DNA,
 * an existing DNA sequence, or an existing DNA pair, matching `_has_dna_input` in
 * benchling/antibodies/nested_data_creation/domain/validate_ndc_pipeline_input.py.
 */
export function summarizeRows(
  rows: ReadonlyArray<ProteinCreationRow>,
  format: AntibodyFormat,
  startingType: NdcSpreadsheetComponentFormatType,
  dnaPairEntityIds: ReadonlySet<string>
): ProteinCreationRowsSummary {
  const columnNames = generateSpreadsheetTemplateHeaders(format, startingType);
  let nonEmptyRowCount = 0;
  let hasDnaInputs = false;
  for (const row of rows) {
    if (!isNonEmptyRow(row)) {
      continue;
    }
    nonEmptyRowCount += 1;
    if (hasDnaInputs) {
      continue;
    }
    hasDnaInputs = columnNames.some((columnName) => {
      const cellValue = row.nestedComponents[columnName];
      return (
        cellValue != null &&
        classifyCellValue(cellValue, dnaPairEntityIds)?.componentSequenceType === NdcComponentSequenceType.DNA
      );
    });
  }
  return {hasData: nonEmptyRowCount > 0, nonEmptyRowCount, hasDnaInputs};
}

function buildComponentFormatKey(
  columnName: string,
  startingType: NdcSpreadsheetComponentFormatType,
  pairLabelToPair: Map<string, {vhVaVgLabel: string; vlVbVdLabel: string}>
): TaggedDomainKeyDict | TaggedChainKeyDict | TaggedVariablePairKeyDict {
  switch (startingType) {
    case NdcSpreadsheetComponentFormatType.DOMAINS:
      return {keyType: 'DOMAIN', label: columnName};
    case NdcSpreadsheetComponentFormatType.CHAINS:
      return {keyType: 'CHAIN', label: columnName};
    case NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS: {
      const pair = pairLabelToPair.get(columnName);
      if (pair != null) {
        return {
          keyType: 'VARIABLE_PAIR',
          vhVaVg: {label: pair.vhVaVgLabel},
          vlVbVd: {label: pair.vlVbVdLabel},
        };
      }
      return {keyType: 'DOMAIN', label: columnName};
    }
    default:
      return {keyType: 'DOMAIN', label: columnName};
  }
}

interface ComponentColumnKey {
  columnName: string;
  componentFormatKey: NdcColumnState['componentFormatKey'];
}

function buildComponentColumnKeys(
  format: AntibodyFormat,
  startingType: NdcSpreadsheetComponentFormatType
): Array<ComponentColumnKey> {
  const pairLabelToPair = new Map<string, {vhVaVgLabel: string; vlVbVdLabel: string}>();
  for (const pair of format.distinctVariablePairs) {
    const label = getVariablePairLabelFromDomainLabels(pair.vhVaVg.label, pair.vlVbVd.label);
    pairLabelToPair.set(label, {vhVaVgLabel: pair.vhVaVg.label, vlVbVdLabel: pair.vlVbVd.label});
  }
  return generateSpreadsheetTemplateHeaders(format, startingType).map((columnName) => ({
    columnName,
    componentFormatKey: buildComponentFormatKey(columnName, startingType, pairLabelToPair),
  }));
}

interface ClassifiedCell extends ComponentColumnKey {
  cellValue: Optional<ProteinCreationCellValue>;
  componentType: NdcComponentType | null;
}

/**
 * Empty cells create nothing. The count helpers require every chain and pair column to be present, and treat
 * an existing AA component as creating nothing.
 */
const EMPTY_CELL_COMPONENT_TYPE: NdcComponentType = {
  componentSequenceType: NdcComponentSequenceType.AA,
  componentInputType: NdcComponentInputType.EXISTING_ENTITY_NAME_OR_REGISTRY_ID,
};

/** Build a single-row interpretation whose column types come from that row's cells. */
function buildNdcInterpretationForRow(cells: ReadonlyArray<ClassifiedCell>): NdcSpreadsheetInterpretation {
  const columns: Array<NdcColumnState> = cells.map(
    ({columnName, componentFormatKey, cellValue, componentType}) => ({
      columnName,
      sampleValue: cellValue?.type === ProteinCreationCellValueType.RAW_TEXT ? cellValue.text : '',
      componentFormatKey,
      inferredTypes: [componentType ?? EMPTY_CELL_COMPONENT_TYPE],
      selectedType: null,
    })
  );
  return {columns, rowCount: 1};
}

function getDomainLabelsForFormatKey(
  componentFormatKey: NdcColumnState['componentFormatKey']
): Array<string> {
  switch (componentFormatKey.keyType) {
    case 'DOMAIN':
      return [componentFormatKey.label];
    case 'VARIABLE_PAIR':
      return [componentFormatKey.vhVaVg.label, componentFormatKey.vlVbVd.label];
    default:
      return [];
  }
}

/**
 * Count the DNA chains concatenated from a row's domain and pair cells. A chain is concatenated when every
 * provided domain has DNA; empty optional domains are skipped. Matches chain concatenation in
 * benchling/antibodies/nested_data_creation/domain/ndc_concatenation_handler.py.
 */
function countDnaChainsFromDomains(
  format: AntibodyFormat,
  optionalDomainLabels: ReadonlySet<string>,
  cells: ReadonlyArray<ClassifiedCell>
): number {
  const providedDomainLabels = new Set<string>();
  const dnaDomainLabels = new Set<string>();
  for (const {componentFormatKey, componentType} of cells) {
    if (componentType == null) {
      continue;
    }
    for (const label of getDomainLabelsForFormatKey(componentFormatKey)) {
      providedDomainLabels.add(label);
      if (componentType.componentSequenceType === NdcComponentSequenceType.DNA) {
        dnaDomainLabels.add(label);
      }
    }
  }
  return format.chains.filter((chain) => {
    const chainDomainLabels = chain.domains
      .map(({label}) => label)
      .filter((label) => !optionalDomainLabels.has(label) || providedDomainLabels.has(label));
    return chainDomainLabels.length > 0 && chainDomainLabels.every((label) => dnaDomainLabels.has(label));
  }).length;
}

/**
 * Count the entities that will be created for each type.
 */
export function getCountsOfCreatedComponentsFromRows(
  rows: ReadonlyArray<ProteinCreationRow>,
  format: AntibodyFormat,
  startingType: NdcSpreadsheetComponentFormatType,
  dnaPairEntityIds: ReadonlySet<string>
): CountsOfCreatedComponents {
  const columnKeys = buildComponentColumnKeys(format, startingType);
  const optionalDomainLabels = new Set(
    format.domains.filter(({optional}) => optional).map(({label}) => label)
  );
  const totals: Omit<CountsOfCreatedComponents, 'totalEntities'> = {
    dnaDomains: 0,
    aaDomains: 0,
    dnaChains: 0,
    aaChains: 0,
    dnaPairs: 0,
    aaPairs: 0,
    antibodies: 0,
  };
  for (const row of rows) {
    if (!isNonEmptyRow(row)) {
      continue;
    }
    const cells: Array<ClassifiedCell> = columnKeys.map((columnKey) => {
      const cellValue = row.nestedComponents[columnKey.columnName];
      return {
        ...columnKey,
        cellValue,
        componentType: cellValue != null ? classifyCellValue(cellValue, dnaPairEntityIds) : null,
      };
    });
    const rowCounts = getCountsOfCreatedComponents(buildNdcInterpretationForRow(cells), startingType, format);
    totals.dnaDomains += rowCounts.dnaDomains;
    totals.aaDomains += rowCounts.aaDomains;
    // The shared count requires DNA on every domain of a chain, including empty optional domains.
    totals.dnaChains +=
      startingType === NdcSpreadsheetComponentFormatType.CHAINS
        ? rowCounts.dnaChains
        : countDnaChainsFromDomains(format, optionalDomainLabels, cells);
    totals.aaChains += rowCounts.aaChains;
    totals.dnaPairs += rowCounts.dnaPairs;
    totals.aaPairs += rowCounts.aaPairs;
    totals.antibodies += rowCounts.antibodies;
  }
  const totalEntities = Object.values(totals).reduce((sum, count) => sum + count, 0);
  return {...totals, totalEntities};
}
